import { cancelQueued, mutate } from '../api'
import { inStoreApp } from '../store-app'
import { TRADE_OPTIONS, type Trade } from '../trade'
import { MESSAGE_BY_OPTIONS, type MessageBy } from '../contact'
import { forgetSignupDraft, signupDraft, tradeFromTag } from '../signup-draft'
import { clear, el, PRIVACY_URL } from '../ui'
import { forgetSignupFrom, signupFrom } from '../signup-from'
import { freeMonthsWords, offerFromTag } from '../../functions/lib/free-time'
import { PLACES_GONE_WORDS, REFERRAL_UNKNOWN_WORDS, whenFirst100Gone } from '../first-100'
import { getMe } from '../me'
import { UK_ZONES, usZoneFor } from '../round-config'

// Set up your business (role 'none' + canSignUp, contract §4 §5 §8) — the
// very first screen for an email nobody has added yet, when signups are
// open. One form, one POST, then the app reloads into the new, empty round.
//
// POST /api/signup answers 201 { businessId } (no `row`), which is not the
// shape mutate()/send() reads as a saved ack (they require row.id) — see
// the manager report. Status 201 is treated as success here regardless of
// `ack.ok` so a real success is never shown as a failure. Confirmed live:
// without the cancelQueued() below, the un-recognised 201 parks the intent
// in the retry queue; the reload lands as the NEW owner and the queue's
// next flush replays the signup under that identity, which 409s
// business_mismatch and shows the fresh owner a false "signed in as a
// different business" banner. Cancelling it the moment we know it landed
// avoids that; the proper fix is the server change named in the report.

export function mountSetup(): void {
  const root = document.querySelector<HTMLElement>('#screen')!
  clear(root)
  // Inside the iPhone app new rounds are not set up (Liam 2026-09-26: sign-up
  // and payment live on the website; the app is for signing in). Apple's rules
  // for a free app that works with a paid website keep it that way.
  if (inStoreApp()) {
    root.append(
      el('div', { class: 'page-heading' }, el('div', {}, el('h2', {}, 'No round for this email yet'),
        el('p', { class: 'muted' }, 'Working for someone? Ask them to add your email in People.'))),
    )
    return
  }
  // 6 months for the First 100 link while places are left, else 3 (0066, free-time.ts, first-100.ts).
  const ledeWords = (months: string): string =>
    `${months} free, no card needed. This makes you the owner of a brand new round. If you already use Roundly, sign in with that email instead.`
  const lede = el('p', { class: 'muted' }, ledeWords(freeMonthsWords(signupFrom())))
  whenFirst100Gone(signupFrom(), (words) => { lede.textContent = ledeWords(words) })
  root.append(
    el(
      'div',
      { class: 'page-heading' },
      el(
        'div',
        {},
        el('h2', {}, 'Set up your round'),
        lede,
      ),
    ),
  )

  const form = el('form', { class: 'add-form setup-form' })
  const err = el('p', { class: 'form-error', role: 'alert' })
  const businessName = el('input', { placeholder: 'e.g. Sam’s Window Cleaning', required: 'required', 'aria-label': 'Business name' }) as HTMLInputElement
  const yourName = el('input', { placeholder: 'e.g. Sam', required: 'required', 'aria-label': 'Your name' })

  // What kind of round (0028): chooses the words the app uses for its jobs.
  // The website page they came from can suggest the kind of round (signup-draft.ts tradeFromTag): shown here,
  // changeable, never saved without this screen.
  let trade: Trade = tradeFromTag(signupFrom()) ?? 'window_cleaning'
  let onTrade = (): void => {}
  businessName.placeholder = `e.g. ${TRADE_OPTIONS.find((t) => t.value === trade)?.example ?? 'Sam’s Window Cleaning'}`
  const trades = el('div', { class: 'setup-trades', role: 'radiogroup', 'aria-label': 'What kind of round do you run?' })
  const drawTrades = (): void => {
    trades.replaceChildren(
      ...TRADE_OPTIONS.map((t) => {
        const b = el('button', { type: 'button', class: t.value === trade ? 'setup-trade setup-trade-on' : 'setup-trade', role: 'radio', 'aria-checked': String(t.value === trade) }, t.label)
        b.addEventListener('click', () => {
          trade = t.value
          businessName.placeholder = `e.g. ${t.example}`
          drawTrades()
          onTrade()
        })
        return b
      }),
    )
  }
  drawTrades()

  // How they usually message customers (0036). Text first: every phone has it (Liam, 30 Sep 2026: "not
  // everyone has whatsapp"). One customer can still go another way, from the ▾ beside any send button.
  // Their answer from before the email, when there is one (signup-draft.ts), filled in here to confirm.
  let messageBy: MessageBy = signupDraft()?.message_by ?? 'text'
  const ways = el('div', { class: 'setup-trades', role: 'radiogroup', 'aria-label': 'How do you usually message customers?' })
  const drawWays = (): void => {
    ways.replaceChildren(
      ...MESSAGE_BY_OPTIONS.map((o) => {
        const b = el('button', { type: 'button', class: o.value === messageBy ? 'setup-trade setup-trade-on' : 'setup-trade', role: 'radio', 'aria-checked': String(o.value === messageBy) }, o.label)
        b.addEventListener('click', () => {
          messageBy = o.value
          drawWays()
        })
        return b
      }),
    )
  }
  drawWays()

  // Three more questions, one tap each and all optional (Liam picked B, 15 Oct 2026:
  // https://claude.ai/artifact/FLSJyvYH1LanhJSJdu5Xnt): who they work with, how they price, how often they go back.
  let worksWith: 'alone' | 'others' | null = null
  let priceMethod: 'per_window' | 'house_size' | 'by_area' | 'none' | null = null
  let usualDays: number | 'varies' | null = null
  const radios = <T,>(label: string, options: [T, string][], get: () => T | null, set: (v: T) => void): HTMLElement => {
    const box = el('div', { class: 'setup-trades', role: 'radiogroup', 'aria-label': label })
    const draw = (): void => {
      box.replaceChildren(...options.map(([value, words]) => {
        const on = get() === value
        const b = el('button', { type: 'button', class: on ? 'setup-trade setup-trade-on' : 'setup-trade', role: 'radio', 'aria-checked': String(on) }, words)
        b.addEventListener('click', () => { set(value); draw() })
        return b
      }))
    }
    draw()
    return box
  }
  // "Just me" (0078; Liam, 19 Oct 2026: "Surely we do this as part of the onboarding"): the simpler app, and the days
  // they usually work, Monday to Friday ticked, which become their working days so Messages has days to offer customers.
  const workDays = new Set([1, 2, 3, 4, 5])
  const daysBox = el('div', { class: 'booking-field setup-work-days' })
  const drawDays = (): void => {
    daysBox.hidden = worksWith !== 'alone'
    const order = [1, 2, 3, 4, 5, 6, 0]
    const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const row = el('div', { class: 'setup-trades', role: 'group', 'aria-label': 'Which days do you usually work?' })
    for (const d of order) {
      const on = workDays.has(d)
      const b = el('button', { type: 'button', class: on ? 'setup-trade setup-trade-on' : 'setup-trade', 'aria-pressed': String(on) }, names[d]!)
      b.addEventListener('click', () => { if (workDays.has(d)) workDays.delete(d); else workDays.add(d); drawDays() })
      row.append(b)
    }
    daysBox.replaceChildren('Which days do you usually work?', row, el('small', { class: 'muted' }, 'Customers are offered these days. Change them any time in My working days.'))
  }
  const team = radios<'alone' | 'others'>('Do you work alone or with others?', [['alone', 'Just me'], ['others', 'Me and others']], () => worksWith, (v) => { worksWith = v; drawDays() })
  drawDays()
  const pricing = radios<'per_window' | 'house_size' | 'by_area' | 'none'>('How do you price?',
    [['per_window', 'Per window'], ['house_size', 'By house size'], ['by_area', 'By size (m²)'], ['none', 'I just know my prices']], () => priceMethod, (v) => { priceMethod = v })
  // A patio, roof and driveway round comes back in years; every other round in weeks.
  const often = el('div')
  const drawOften = (): void => {
    const choices: [number | 'varies', string][] = trade === 'exterior'
      ? [[365, 'Every year'], [730, '2 years'], [1095, '3 years'], ['varies', 'It varies']]
      : [[28, '4 weeks'], [56, '8 weeks'], [84, '12 weeks'], ['varies', 'It varies']]
    if (usualDays !== null && !choices.some(([v]) => v === usualDays)) usualDays = null
    often.replaceChildren(radios<number | 'varies'>('How often do you usually go back?', choices, () => usualDays, (v) => { usualDays = v }))
  }
  drawOften()
  onTrade = drawOften

  // Where the round is (Step 2 round 4): asked only when the US is open as well as the UK. The country sets the money,
  // the dates and the phone numbers; the time zone sets the round's day and every reminder's clock. Picked for them only
  // when the phone's own clock says so: London is the UK; any US zone (Detroit and Indianapolis too, not only the seven
  // named; Liam, 10 Oct 2026: "you would set them wrong initially for us customers?") is the US and its matching zone.
  // A phone anywhere else picks nothing, and the owner must choose. Never from the internet address.
  const open = getMe()?.countries ?? {}
  const usZones: string[] = Array.isArray(open.US) ? open.US : []
  const ZONE_NAMES: Record<string, string> = { 'America/New_York': 'Eastern', 'America/Chicago': 'Central', 'America/Denver': 'Mountain',
    'America/Phoenix': 'Arizona', 'America/Los_Angeles': 'Pacific', 'America/Anchorage': 'Alaska', 'Pacific/Honolulu': 'Hawaii' }
  let phoneZone = ''
  try { phoneZone = Intl.DateTimeFormat().resolvedOptions().timeZone } catch { /* no zone: the owner picks */ }
  const phoneUS = usZones.length ? usZoneFor(phoneZone, usZones) : null
  let country: 'GB' | 'US' | null = phoneUS ? 'US' : UK_ZONES.has(phoneZone) || !usZones.length ? 'GB' : null
  let zone: string | null = phoneUS
  const where = el('div', { class: 'setup-trades', role: 'radiogroup', 'aria-label': 'Where is your round?' })
  const zones = el('div', { class: 'setup-trades', role: 'radiogroup', 'aria-label': 'Which time zone?' })
  const zoneField = el('div', { class: 'booking-field' }, 'Which time zone?', zones)
  // What the pick means, said under it, so a wrong one is plain to see before anything is set up.
  const means = el('p', { class: 'field-help' })
  const drawWhere = (): void => {
    means.textContent = country === 'US' ? 'Dollars, ZIP codes, and dates like October 13.' : country === 'GB' ? 'Pounds, postcodes, and dates like 13 October.' : 'This sets your money, dates and phone numbers.'
    where.replaceChildren(...([['GB', 'United Kingdom'], ['US', 'United States']] as const).map(([value, label]) => {
      const b = el('button', { type: 'button', class: value === country ? 'setup-trade setup-trade-on' : 'setup-trade', role: 'radio', 'aria-checked': String(value === country) }, label)
      b.addEventListener('click', () => { country = value; drawWhere() })
      return b
    }))
    zones.replaceChildren(...usZones.map((z) => {
      const b = el('button', { type: 'button', class: z === zone ? 'setup-trade setup-trade-on' : 'setup-trade', role: 'radio', 'aria-checked': String(z === zone) }, ZONE_NAMES[z] ?? z)
      b.addEventListener('click', () => { zone = z; drawWhere() })
      return b
    }))
    zoneField.hidden = country !== 'US'
  }
  drawWhere()
  if (usZones.length) form.append(el('div', { class: 'booking-field' }, 'Where is your round?', where, means), zoneField)

  form.append(
    el('div', { class: 'booking-field' }, 'What kind of round do you run?', trades),
    el('div', { class: 'booking-field' }, 'How do you usually message customers?', ways,
      el('p', { class: 'field-help' }, 'Roundly writes the message and opens it ready to send. You can change this for one customer, or for everyone later in Your business.')),
    el('div', { class: 'booking-field' }, 'Do you work alone or with others?', team),
    daysBox,
    el('div', { class: 'booking-field' }, 'How do you price?', pricing),
    el('div', { class: 'booking-field' }, 'How often do you usually go back?', often,
      el('p', { class: 'field-help' }, 'A new customer starts on this. You can change it for anyone.')),
    el('label', { class: 'booking-field' }, 'Business name', businessName),
    el('label', { class: 'booking-field' }, 'Your name', yourName,
      el('p', { class: 'field-help' }, 'Goes on your jobs, so your day shows on a Today tab. If you only run the office, you can clear it later in People.')),
    err,
  )
  const submit = el('button', { type: 'submit', class: 'btn-primary' }, 'Set up my round')
  form.append(submit)
  form.append(el('p', { class: 'field-help setup-privacy' }, el('a', { href: PRIVACY_URL, target: '_blank', rel: 'noopener' }, 'How Roundly looks after your information')))

  form.addEventListener('submit', (e) => {
    e.preventDefault()
    err.textContent = ''
    const name = businessName.value.trim()
    const you = yourName.value.trim()
    if (!name) {
      err.textContent = 'Type your business name.'
      return
    }
    if (!you) {
      err.textContent = 'Type your name.'
      return
    }
    if (!country) {
      err.textContent = 'Pick where your round is.'
      return
    }
    if (country === 'US' && !zone) {
      err.textContent = 'Pick your time zone.'
      return
    }
    submit.setAttribute('disabled', 'disabled')
    submit.textContent = 'Setting up…'
    let queuedKey: string | null = null
    const signupFromAtSend = signupFrom()
    const askedOffer = offerFromTag(signupFromAtSend)
    void mutate({
      label: `Set up: ${name}`,
      method: 'POST',
      path: '/api/signup',
      // Where they came from, when the website's link said (signup-from.ts).
      body: { business_name: name, your_name: you, trade, message_by: messageBy, ...(country === 'US' ? { country, time_zone: zone } : {}), ...(signupFrom() ? { from: signupFrom() } : {}),
        ...(worksWith ? { works_with: worksWith } : {}), ...(worksWith === 'alone' && workDays.size ? { work_days: [...workDays].sort() } : {}), ...(priceMethod && priceMethod !== 'none' ? { price_method: priceMethod } : {}), ...(typeof usualDays === 'number' ? { default_cycle_days: usualDays } : {}) },
      onQueued: (m) => {
        queuedKey = m.key
      },
    }).then((ack) => {
      // See the note above the shape mismatch: a 201 IS a save.
      if (ack.ok || ack.status === 201) {
        if (queuedKey) cancelQueued(queuedKey)
        // Sent with this round, so a later sign-up on this phone starts clean: forgotten, and taken
        // off the address too, or the reload below would keep it again (signup-from.ts).
        forgetSignupFrom()
        forgetSignupDraft()
        // Straight on to getting their customers in (#/start). If the last First 100 place went while this was being
        // filled in, say so first: the round has the usual 3 months (the server decides, signup.ts).
        const go = (): void => {
          window.history.replaceState(null, '', `${window.location.pathname}#/start`)
          window.location.reload()
        }
        const given = (ack.row as { free_offer?: string } | undefined)?.free_offer
        // A First 100 link whose places went meanwhile, or a referral link (ref-...) whose code wasn't found: said, and
        // kept on screen until they carry on (Astra: never a note that disappears by itself).
        const askedReferral = (signupFromAtSend ?? '').startsWith('ref-')
        if (given && ((askedOffer === 'first-100' && given !== 'first-100') || (askedReferral && given !== 'referred'))) {
          submit.textContent = 'Set up'
          err.classList.add('setup-note')
          err.textContent = askedOffer === 'first-100' ? PLACES_GONE_WORDS : REFERRAL_UNKNOWN_WORDS
          const carryOn = el('button', { type: 'button', class: 'btn-primary' }, 'Carry on')
          carryOn.addEventListener('click', go)
          err.after(carryOn)
          return
        }
        go()
        return
      }
      submit.removeAttribute('disabled')
      submit.textContent = 'Set up my round'
      if (ack.status === 409) {
        err.textContent = 'This email already has a Roundly round. Reload the page and sign in again.'
      } else if (ack.status >= 400 && ack.status < 500) {
        err.textContent = ack.error ?? 'Check what you typed and try again.'
      } else {
        err.textContent = "Couldn't save. Check your connection and try again."
      }
    })
  })

  root.append(form)
}

